import { useRouteStore } from '@/stores/routeStore'
import { IProjectService } from '../IProjectService'
import { ISnapshotService } from '../ISnapshotService'
import { IArtifactService } from '../IArtifactService'
import { IGlobalFilterService } from '../IGlobalFilterService'
import {
  IRouteParamValidator,
  ValidationResult,
  RouteValidationResult,
  ValidationError,
} from '../IRouteParamValidator'
import { asSnapshotReference } from '@/models'
import labels from '@/translations/labels.json'

/**
 * Implementation of RouteParamValidator
 *
 * Pure validation service with no side effects.
 * Validates route parameters against backend services.
 */
export default class RouteParamValidator implements IRouteParamValidator {
  constructor(
    private projectService: IProjectService,
    private snapshotService: ISnapshotService,
    private artifactService: IArtifactService,
    private globalFilterService: IGlobalFilterService,
  ) {}

  async validateProjectId(projectId: string): Promise<ValidationResult> {
    try {
      const projectIdNum = parseInt(projectId)
      const project = await this.projectService.getProjectById(projectIdNum)

      if (project !== undefined) {
        return {
          isValid: true,
          data: project,
        }
      } else {
        return this.createErrorResult(
          'INVALID_PROJECT_ID',
          labels.ALERT_VIEW.INVALID_PROJECT_ID,
          projectId,
        )
      }
    } catch {
      return this.createErrorResult(
        'INVALID_PROJECT_ID',
        labels.ALERT_VIEW.INVALID_PROJECT_ID,
        projectId,
      )
    }
  }

  async validateSnapshotId(projectId: string, snapshotId: string): Promise<ValidationResult> {
    try {
      const projectIdNum = parseInt(projectId)
      const snapshot = await this.snapshotService.getSnapshot(
        projectIdNum,
        asSnapshotReference(snapshotId),
      )

      // Check if snapshot ID matches or is 'latest'
      if (snapshot.data.id.toString() === snapshotId || snapshotId === 'latest') {
        return {
          isValid: true,
          data: snapshot,
        }
      } else {
        return this.createErrorResult(
          'INVALID_SNAPSHOT_ID',
          labels.ALERT_VIEW.INVALID_SNAPSHOT_ID,
          snapshotId,
        )
      }
    } catch {
      return this.createErrorResult(
        'INVALID_SNAPSHOT_ID',
        labels.ALERT_VIEW.INVALID_SNAPSHOT_ID,
        snapshotId,
      )
    }
  }

  async validateArtifactId(
    projectId: string,
    snapshotId: string,
    artifactId: string,
    filterId?: string,
  ): Promise<ValidationResult> {
    try {
      const projectIdNum = parseInt(projectId)
      const artifactIdNum = parseInt(artifactId)
      const filterIdNum = filterId ? parseInt(filterId) : undefined

      const artifact = await this.artifactService.getArtifact(
        projectIdNum,
        asSnapshotReference(snapshotId),
        artifactIdNum,
        filterIdNum,
      )

      if (artifact !== undefined) {
        return {
          isValid: true,
          data: artifact,
        }
      } else {
        return this.createErrorResult(
          'INVALID_ARTIFACT_ID',
          labels.ALERT_VIEW.INVALID_ARTIFACT_ID,
          artifactId,
        )
      }
    } catch {
      return this.createErrorResult(
        'INVALID_ARTIFACT_ID',
        labels.ALERT_VIEW.INVALID_ARTIFACT_ID,
        artifactId,
      )
    }
  }

  async validateFilterId(projectId: string, filterId: string): Promise<ValidationResult> {
    try {
      const projectIdNum = parseInt(projectId)
      const filters = await this.globalFilterService.getGlobalFilters(projectIdNum)

      const filterExists = filters.some((filter) => filter.id.toString() === filterId)

      if (filterExists) {
        return {
          isValid: true,
        }
      } else {
        return this.createErrorResult(
          'INVALID_FILTER_ID',
          labels.ALERT_VIEW.INVALID_FILTER_ID,
          filterId,
        )
      }
    } catch {
      return this.createErrorResult(
        'INVALID_FILTER_ID',
        labels.ALERT_VIEW.INVALID_FILTER_ID,
        filterId,
      )
    }
  }

  async validateAllRouteParams(): Promise<RouteValidationResult> {
    const routeStore = useRouteStore()

    const result: RouteValidationResult = {
      projectId: { isValid: false },
      isAllValid: false,
      hasValidProject: false,
    }

    // Validate projectId (required)
    if (routeStore.projectId !== undefined) {
      result.projectId = await this.validateProjectId(routeStore.projectId)
      result.hasValidProject = result.projectId.isValid
    } else {
      result.projectId = this.createErrorResult('MISSING_PROJECT_ID', 'Project ID is required', '')
      result.hasValidProject = false
    }

    // If project is invalid, don't validate other params
    if (!result.hasValidProject) {
      result.isAllValid = false
      return result
    }

    // Validate optional params if they exist
    const validationPromises: Promise<void>[] = []

    if (routeStore.snapshotId !== undefined && routeStore.projectId !== undefined) {
      validationPromises.push(
        this.validateSnapshotId(routeStore.projectId, routeStore.snapshotId).then(
          (snapshotResult) => {
            result.snapshotId = snapshotResult
          },
        ),
      )
    }

    if (
      routeStore.artifactId !== undefined &&
      routeStore.snapshotId !== undefined &&
      routeStore.projectId !== undefined
    ) {
      validationPromises.push(
        this.validateArtifactId(
          routeStore.projectId,
          routeStore.snapshotId,
          routeStore.artifactId,
          routeStore.filterId,
        ).then((artifactResult) => {
          result.artifactId = artifactResult
        }),
      )
    }

    if (routeStore.filterId !== undefined && routeStore.projectId !== undefined) {
      validationPromises.push(
        this.validateFilterId(routeStore.projectId, routeStore.filterId).then((filterResult) => {
          result.filterId = filterResult
        }),
      )
    }

    // Wait for all validations to complete
    await Promise.all(validationPromises)

    // Determine if all present params are valid
    result.isAllValid =
      result.projectId.isValid &&
      (result.snapshotId?.isValid ?? true) &&
      (result.artifactId?.isValid ?? true) &&
      (result.filterId?.isValid ?? true)

    return result
  }

  async checkForSnapshots(projectId: string): Promise<boolean> {
    try {
      const projectIdNum = parseInt(projectId)
      const snapshots = await this.snapshotService.getSnapshots(projectIdNum)
      return Object.entries(snapshots.data).length > 0
    } catch {
      return false
    }
  }

  private createErrorResult(code: string, message: string, value: string): ValidationResult {
    const error: ValidationError = {
      code,
      message,
      value,
    }
    return {
      isValid: false,
      error,
    }
  }
}
